import axios from "axios";
import { URL_BASE } from '../const/config';

// 認証トークンを取得する関数
const getAccessToken = () => {
  return localStorage.getItem('accessToken');
};

// 認証ヘッダーを生成する関数
const getAuthHeaders = () => {
  const token = getAccessToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// トークンリフレッシュの関数
export const refreshAuthToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('リフレッシュトークンがありません');
  }

  try {
    const response = await axios.post(URL_BASE + '/api/auth/refresh', {}, {
      headers: {
        'Authorization': `Bearer ${refreshToken}`,
        'Content-Type': 'application/json'
      }
    });

    // 新しいトークンをストレージに保存
    localStorage.setItem('accessToken', response.data.access_token);
    if (response.data.refresh_token) {
      localStorage.setItem('refreshToken', response.data.refresh_token);
    }

    return true;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    throw error;
  }
};

// GETリクエスト
export const fetchData = async (endpoint) => {
  try {
    const response = await axios.get(URL_BASE + endpoint, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching data from ${endpoint}:`, error);
    
    // トークン期限切れの場合、トークン更新を試みる
    if (error.response?.status === 401) {
      try {
        // トークンのリフレッシュを試行
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          // リフレッシュ成功後、元のリクエストを再試行
          return fetchData(endpoint);
        }
      } catch (refreshError) {
        // リフレッシュが失敗した場合、ログイン画面へリダイレクト
        window.location.href = '/login?message=session_expired';
      }
    }
    
    // エラーを整形して再スロー
    const enhancedError = {
      message: error.response?.data?.message || "データ取得に失敗しました",
      status: error.response?.status,
      originalError: error,
      endpoint
    };
    
    throw enhancedError;
  }
};

export const postCreateFile = async (endpoint, data) => {
  try {
    const response = await axios.post(URL_BASE + endpoint, data, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      responseType: "blob"
    });
    return response.data;
  } catch (error) {
    console.error(`Error posting data to ${endpoint}:`, error);
    const enhancedError = {
      message: error.response?.data?.message || "サーバーとの通信に失敗しました",
      status: error.response?.status,
      originalError: error,
      endpoint
    };
    
    throw enhancedError;
  }
};

export const postCreateFileNoData = async (endpoint) => {
  try {
    const response = await axios.post(URL_BASE + endpoint, null, {
      headers: {
        ...getAuthHeaders(),
      },
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    console.error(`Error posting data to ${endpoint}:`, error);
    throw error;
  }
};

export const postDownloadFile = async (endpoint, data) => {
  try {
    const response = await axios.post(URL_BASE + endpoint, data, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      responseType: "blob"
    });
    return response;
  } catch (error) {
    console.error(`Error posting data to ${endpoint}:`, error);
    const enhancedError = {
      message: error.response?.data?.message || "サーバーとの通信に失敗しました",
      status: error.response?.status,
      originalError: error,
      endpoint
    };
    
    throw enhancedError;
  }
};

// POSTリクエスト - JWT対応版
export const postData = async (endpoint, data={}) => {
  try {
    const response = await axios.post(URL_BASE + endpoint, data, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error posting data to ${endpoint}:`, error);
    
    // 認証エンドポイントの場合はトークン更新を試みない
    const authEndpoints = ['api/auth/login', 'api/auth/logout', 'api/auth/refresh'];
    
    // トークン期限切れの場合、かつ認証エンドポイント以外の場合にトークン更新を試みる
    if (error.response?.status === 401 && !authEndpoints.includes(endpoint)) {
      try {
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          return postData(endpoint, data);
        }
      } catch (refreshError) {
        window.location.href = '/login?message=session_expired';
        return; // この後のコードを実行しない
      }
    }
    
    // エラーを適切に整形して再スロー
    const enhancedError = {
      message: error.response?.data?.message || "サーバーとの通信に失敗しました",
      status: error.response?.status,
      originalError: error,
      endpoint
    };
    
    throw enhancedError; // エラーを呼び出し元に伝える
  }
};

export const uploadFormData = async (endpoint, formData) => {
  try {
    const response = await fetch(URL_BASE + endpoint, {
      headers: {
        ...getAuthHeaders(),
      },
      method: "POST",
      mode: "cors",
      body: formData,
    });

    // 認証エラーの場合
    if (response.status === 401) {
      try {
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          // トークン更新成功後、再試行
          return uploadFormData(endpoint, formData);
        }
      } catch (refreshError) {
        window.location.href = '/login?message=session_expired';
        return;
      }
    }

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      // エラーレスポンスの処理
      const errorData = await response.json().catch(() => ({}));
      const enhancedError = {
        message: errorData.message || "サーバーとの通信に失敗しました",
        status: response.status,
        endpoint
      };
      throw enhancedError;
    }
  } catch (error) {
    console.error(`Error posting data to ${endpoint}:`, error);

    const enhancedError = {
      message: error.message || "サーバーとの通信に失敗しました",
      status: error.status,
      originalError: error,
      endpoint
    };
    
    throw enhancedError;
  }
};

// DELETEリクエスト - JWT対応版
export const deleteData = async (endpoint) => {
  try {
    const response = await axios.delete(URL_BASE + endpoint, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json"
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error deleting data from ${endpoint}:`, error);
    
    // トークン期限切れの場合、トークン更新を試みる
    if (error.response?.status === 401) {
      try {
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          return deleteData(endpoint);
        }
      } catch (refreshError) {
        window.location.href = '/login?message=session_expired';
      }
    }
    
    // エラーを整形して再スロー
    const enhancedError = {
      message: error.response?.data?.message || "削除に失敗しました",
      status: error.response?.status,
      originalError: error,
      endpoint
    };
    
    throw enhancedError;
  }
};

// ログアウト時にトークンを無効化
export const logoutAndInvalidateToken = async () => {
  const accessToken = getAccessToken();
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (!accessToken) return true;
  
  try {
    await postData('/api/auth/logout', { refresh_token: refreshToken });
    return true;
  } catch (error) {
    console.error('Error during logout:', error);
    return false;
  }
};

export const postUserInfo = async (uuid, tel, email, password) => {
  const userInfoData = { uuid, tel, email };
  if (password) userInfoData.password = password;

  return postData(`api/post_user_info`, userInfoData);
};